import React, { useEffect, useMemo, useState } from 'react';
import { IPanelHeaderRenderer, IPanelProps, Panel, PanelType } from '@fluentui/react/lib/Panel';
import { CrLoadingOverlay } from '../cr/CrLoadingOverlay';
import { ViewFormCommandBar } from '../cr/ViewFormCommandBar';
import { IMetric } from '../../types';
import styles from '../../styles/cr.module.scss';

interface MetricViewProps {
    entityName?: string;
    showForm?: boolean;
    entityId?: number;
    onClosed?: () => void;
    loadEntity: (entityId: number) => Promise<IMetric>;
}

export const MetricView: React.FC<MetricViewProps> = ({ showForm, entityName, entityId, loadEntity, onClosed }) => {
    const [formData, setFormData] = useState<IMetric>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const entity = await loadEntity(entityId);
                setFormData(entity);
                setLoading(false);
            } catch (error) {
                console.error('Error loading entity:', error);
                setLoading(false);
            }
        };

        fetchData();
    }, [loadEntity]);

    const metricStaff = useMemo(() => {
        if (!formData) return '';

        const staffRoles = [
            { role: 'Lead', users: formData.LeadUser ? [`${formData.LeadUser.Title} (${formData.LeadUser.EmailAddress})`] : [] },
            {
                role: 'Contributors',
                users: formData.Contributors && formData.Contributors.some(c => c.ContributorUser != null)
                    ? formData.Contributors.map(mc => `${mc.ContributorUser?.Title} (${mc.ContributorUser?.EmailAddress})`)
                    : [],
            },
        ];

        const formattedStaffRoles = staffRoles
            .filter(role => role.users.length > 0)
            .map(role => `<div style='display:flex'><div style='font-weight:bold'>-&nbsp;</div><div>${role.role}: ${role.users.join(', ')}</div></div>`)
            .join('');

        return formattedStaffRoles;
    }, [formData]);


    const headerText = useMemo(() => {
        if (!formData) return entityName;

        return `${entityName} - ${formData.Title}`;
    }, [entityName, formData]);

    const formattedDescription = useMemo(() => {
        if (!formData) return null;

        return formData.Description ? formData.Description.replace(/\n/g, '<br>') : null;
    }, [formData]);

    return (
        <Panel
            className={styles.cr}
            isOpen={showForm}
            headerText={headerText}
            type={PanelType.medium}
            hasCloseButton={false}
            onRenderHeader={(props: IPanelProps, defaultRender: IPanelHeaderRenderer) =>
                <div style={{ marginBottom: '20px' }} className={styles.panelHeaderText}>{defaultRender(props)}</div>
            }
            onRenderNavigation={() =>
                <ViewFormCommandBar onClose={onClosed} />
            }
        >
            <div className={styles.cr}>
                <CrLoadingOverlay isLoading={loading} opaque={true} />
                {formData && (
                    <div>
                        <div className={styles.viewFormFieldLabel}>Metric ID</div>
                        <div className={styles.viewFormFieldData}>{formData.MetricCode || 'N/A'}</div>

                        <div className={styles.viewFormFieldLabel}>Target performance unit</div>
                        <div className={styles.viewFormFieldData}>{formData.MeasurementUnit?.Title || 'N/A'}</div>

                        {/* <div className={styles.viewFormFieldLabel}>Target performance limit</div>
                        <div className={styles.viewFormFieldData}>
                            {formData.TargetPerformanceLowerLimit != null && formData.TargetPerformanceUpperLimit != null ? (
                                `${formData.TargetPerformanceLowerLimit} to ${formData.TargetPerformanceUpperLimit}`
                            ) : formData.TargetPerformanceLowerLimit != null ? (
                                `≥ ${formData.TargetPerformanceLowerLimit}`
                            ) : formData.TargetPerformanceUpperLimit != null ? (
                                `≤ ${formData.TargetPerformanceUpperLimit}`
                            ) : (
                                'N/A'
                            )}
                        </div> */}

                        <div className={styles.viewFormFieldLabel}>Target performance limit</div>
                        <div className={styles.viewFormFieldData}>
                            {formData.TargetPerformanceLowerLimit != null && formData.TargetPerformanceUpperLimit != null ? (
                                `Range: ${formData.TargetPerformanceLowerLimit} to ${formData.TargetPerformanceUpperLimit}`
                            ) : formData.TargetPerformanceLowerLimit != null ? (
                                `Minimum: ${formData.TargetPerformanceLowerLimit}`
                            ) : formData.TargetPerformanceUpperLimit != null ? (
                                `Maximum: ${formData.TargetPerformanceUpperLimit}`
                            ) : (
                                'N/A'
                            )}
                        </div>

                        <div className={styles.viewFormFieldLabel}>Staff</div>
                        <div className={styles.viewFormFieldData}>
                            {metricStaff ? (
                                <div dangerouslySetInnerHTML={{ __html: metricStaff }} />
                            ) : (
                                'N/A'
                            )}
                        </div>

                        <div className={styles.viewFormFieldLabel}>CustomFields</div>
                        <div className={styles.viewFormFieldData}>
                            {formData.CustomFields && formData.CustomFields.length > 0 ? (
                                formData.CustomFields.map(a => a.CustomFieldDfn?.Title).join(', ')
                            ) : (
                                'N/A'
                            )}
                        </div>

                        <div className={styles.viewFormFieldLabel}>Description</div>
                        <div className={styles.viewFormFieldData}>
                            {formattedDescription ? (
                                <div dangerouslySetInnerHTML={{ __html: formattedDescription }} />
                            ) : (
                                'N/A'
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Panel>
    );
};